import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, X } from "lucide-react";
import type { Friend, SplitGroup } from "@boo/shared";
import { useSplitGroupStore } from "@/stores/useSplitGroupStore";
import FriendPicker from "@/features/friends/components/FriendPicker";
import type { EditorPerson } from "../types/bills.types";
import PersonAvatar from "./PersonAvatar";

interface PeopleSectionProps {
  people: EditorPerson[];
  readOnly: boolean;
  onAddFriend: (friend: Friend) => void;
  onAddGroup: (group: SplitGroup) => void;
  onRemove: (id: string) => void;
  onRelink: (personId: string, friend: Friend) => Promise<void>;
}

export default function PeopleSection({
  people,
  readOnly,
  onAddFriend,
  onAddGroup,
  onRemove,
  onRelink,
}: PeopleSectionProps) {
  const [relinkingId, setRelinkingId] = useState<string | null>(null);
  const { groups, loadGroups } = useSplitGroupStore();

  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  const takenIds = people.flatMap((p) => (p.friendId ? [p.friendId] : []));
  const relinking = people.find((p) => p.id === relinkingId);

  async function relink(personId: string, friend: Friend) {
    try {
      await onRelink(personId, friend);
      setRelinkingId(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to change friend",
      );
    }
  }

  return (
    <section className="space-y-2.5">
      <h2 className="text-sm font-semibold text-foreground">Who was there</h2>
      <div className="flex flex-wrap gap-2">
        {people.map((p) => (
          <span
            key={p.id}
            className={`inline-flex h-9 items-center gap-0.5 rounded-lg border bg-card pl-3 text-xs font-medium text-foreground sm:h-auto sm:py-1.5 ${
              p.id === relinkingId ? "border-primary" : "border-border"
            } ${!p.isOwner ? "pr-1 sm:pr-2" : "pr-3"}`}
          >
            {!p.isOwner && (
              <span className="mr-1.5 flex">
                <PersonAvatar person={p} size={20} />
              </span>
            )}
            {p.name}
            {!p.isOwner && (
              <button
                type="button"
                onClick={() =>
                  setRelinkingId(p.id === relinkingId ? null : p.id)
                }
                aria-label={`Change who ${p.name} is`}
                className="relative flex h-9 w-7 items-center justify-center text-muted-foreground before:absolute before:-inset-1 before:content-[''] hover:text-foreground sm:h-auto sm:w-auto"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
            {!p.isOwner && !readOnly && (
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                aria-label={`Remove ${p.name}`}
                className="relative flex h-9 w-7 items-center justify-center text-muted-foreground before:absolute before:-inset-1 before:content-[''] hover:text-destructive active:text-destructive sm:h-auto sm:w-auto"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        ))}
      </div>
      {relinking && (
        <div className="flex flex-wrap items-center gap-2">
          <FriendPicker
            key={relinking.id}
            placeholder={`Change ${relinking.name} to…`}
            excludeIds={takenIds}
            onPickFriend={(friend) => relink(relinking.id, friend)}
          />
          <button
            type="button"
            onClick={() => setRelinkingId(null)}
            className="h-9 rounded-lg border border-border px-3 text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <FriendPicker
            groups={groups}
            excludeIds={takenIds}
            onPickFriend={onAddFriend}
            onPickGroup={onAddGroup}
            placeholder="Add a friend or group"
          />
        </div>
      )}
    </section>
  );
}
